import { FC, useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import SecurityIcon from '@mui/icons-material/Security'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import { useSnackbar } from 'notistack'
import { PageHeader } from '@/components/common/PageHeader'
import { RecoveryCodesDisplay } from '@/components/common/RecoveryCodesDisplay'
import { authService } from '@/services/authService'
import { TotpStatus } from '@/types/auth'

const SegurancaPage: FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const [status, setStatus] = useState<TotpStatus | null>(null)
  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [novosCodigos, setNovosCodigos] = useState<string[] | null>(null)
  const [regenerando, setRegenerando] = useState(false)

  const carregar = useCallback(async () => {
    setLoading(true)
    setErro(null)
    try {
      setStatus(await authService.totpStatus())
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao carregar status do 2FA')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    carregar()
  }, [carregar])

  const regenerar = async () => {
    if (!window.confirm('Gerar novos códigos? Os códigos atuais deixarão de funcionar.')) return
    setRegenerando(true)
    try {
      const codes = await authService.regenerateRecoveryCodes()
      setNovosCodigos(codes)
      enqueueSnackbar('Novos códigos de recuperação gerados', { variant: 'success' })
      await carregar()
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : 'Falha ao gerar códigos', { variant: 'error' })
    } finally {
      setRegenerando(false)
    }
  }

  return (
    <Box>
      <PageHeader title="Segurança" subtitle="Verificação em duas etapas e códigos de recuperação" />

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      <Paper sx={{ p: 3, maxWidth: 560 }}>
        {loading ? (
          <CircularProgress size={24} />
        ) : status ? (
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <SecurityIcon color={status.enabled ? 'success' : 'disabled'} />
              <Box>
                <Typography fontWeight={600}>Autenticação em duas etapas (2FA)</Typography>
                <Chip
                  size="small"
                  label={status.enabled ? 'Ativa' : 'Inativa'}
                  color={status.enabled ? 'success' : 'default'}
                />
              </Box>
            </Stack>

            {status.enabled ? (
              <>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Códigos de recuperação restantes
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    color={status.recoveryCodesRemaining <= 2 ? 'error.main' : 'text.primary'}
                  >
                    {status.recoveryCodesRemaining}
                  </Typography>
                  {status.recoveryCodesRemaining <= 2 && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Você está com poucos códigos. Gere novos para não perder o acesso.
                    </Alert>
                  )}
                </Box>

                <Button
                  variant="contained"
                  startIcon={<AutorenewIcon />}
                  onClick={regenerar}
                  disabled={regenerando}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {regenerando ? 'Gerando…' : 'Gerar novos códigos'}
                </Button>

                {novosCodigos && (
                  <Box sx={{ mt: 1 }}>
                    <Typography fontWeight={600} sx={{ mb: 1 }}>
                      Seus novos códigos
                    </Typography>
                    <RecoveryCodesDisplay codes={novosCodigos} />
                  </Box>
                )}
              </>
            ) : (
              <Alert severity="info">A verificação em duas etapas será configurada no seu próximo login.</Alert>
            )}
          </Stack>
        ) : null}
      </Paper>
    </Box>
  )
}

export default SegurancaPage
